import type {
  EmailRegistrationPolicy,
  EmailWhitelistPolicy,
  RegistrationFormConfigurationInput,
} from 'src/generated/graphql-manager';

import { LinkStyleButton, SelectionInputLayout, Toggle, Tooltip } from '@movingimage-evp/mi-ui-component-library';
import { t } from 'i18next';
import { useState } from 'react';

import { RegistrationFieldsModal } from '../registration-fields-modal';

import styles from './registration.module.css';

type RegistrationFieldsFormProps = {
  policy?: EmailWhitelistPolicy | EmailRegistrationPolicy;
  disabled: boolean;
  onRegistrationFieldsChange: ({
    enabled,
    registrationForm,
  }: {
    enabled: boolean;
    registrationForm: RegistrationFormConfigurationInput | null;
  }) => void;
  onEmailVerificationChange: () => void;
};

export const defaultRegistrationFormFields = {
  firstAndLastName: false,
  jobTitle: false,
  companyName: false,
  departmentName: false,
  phoneNumber: false,
  country: false,
  bankCode: false,
};

export function RegistrationFieldsForm({
  disabled,
  policy,
  onRegistrationFieldsChange,
  onEmailVerificationChange,
}: RegistrationFieldsFormProps) {
  const [fieldsModalOpen, setFieldsModalOpen] = useState(false);

  const registrationForm = policy?.registrationFormConfiguration || {};
  const registrationEnabled = policy?.__typename === 'EmailWhitelistPolicy' ? true : policy?.enabled;
  const requireEmailVerification = policy?.requireEmailVerification === true;
  const showRegistrationFormToggle = policy?.__typename !== 'EmailWhitelistPolicy';

  const handleRegistrationToggleChange = () => {
    if (registrationEnabled) {
      onRegistrationFieldsChange({ enabled: false, registrationForm: defaultRegistrationFormFields });
    } else {
      setFieldsModalOpen(true);
    }
  };

  const handleConfirm = (registrationForm: RegistrationFormConfigurationInput) => {
    setFieldsModalOpen(false);
    onRegistrationFieldsChange({ enabled: true, registrationForm });
  };

  return (
    <>
      <fieldset className={styles.registration} disabled={disabled}>
        {t('manager.webcastSetup.viewerAccess.registration.title')}
        <div className={styles.registrationWrapper}>
          <div>
            {showRegistrationFormToggle && (
              <SelectionInputLayout
                variant="ghost"
                checked={registrationEnabled}
                inputElement={
                  <Toggle
                    data-testid="enable-registration-toggle"
                    checked={registrationEnabled}
                    onChange={handleRegistrationToggleChange}
                  >
                    {t('manager.webcastSetup.viewerAccess.registration.formToggle')}
                  </Toggle>
                }
              />
            )}
            {registrationEnabled && (
              <div className={styles.form}>
                <div className={styles.formTitle}>
                  {t('manager.webcastSetup.viewerAccess.registration.selectedFieldsTitle')}
                </div>
                <div className={styles.formFields}>
                  {registrationForm.email && (
                    <div data-testid={'field-email'}>
                      {t('manager.webcastSetup.viewerAccess.registration.fieldsOptions.email')}
                    </div>
                  )}
                  {registrationForm.firstAndLastName && (
                    <div data-testid={'field-firstAndLastName'}>
                      {t('manager.webcastSetup.viewerAccess.registration.fieldsOptions.firstAndLastName')}
                    </div>
                  )}
                  {registrationForm.companyName && (
                    <div data-testid={'field-companyName'}>
                      {t('manager.webcastSetup.viewerAccess.registration.fieldsOptions.companyName')}
                    </div>
                  )}
                  {registrationForm.departmentName && (
                    <div data-testid={'field-departmentName'}>
                      {t('manager.webcastSetup.viewerAccess.registration.fieldsOptions.departmentName')}
                    </div>
                  )}
                  {registrationForm.jobTitle && (
                    <div data-testid={'field-jobTitle'}>
                      {t('manager.webcastSetup.viewerAccess.registration.fieldsOptions.jobTitle')}
                    </div>
                  )}
                  {registrationForm.country && (
                    <div data-testid={'field-country'}>
                      {t('manager.webcastSetup.viewerAccess.registration.fieldsOptions.country')}
                    </div>
                  )}
                  {registrationForm.phoneNumber && (
                    <div data-testid={'field-phoneNumber'}>
                      {t('manager.webcastSetup.viewerAccess.registration.fieldsOptions.phoneNumber')}
                    </div>
                  )}
                  {registrationForm.bankCode && (
                    <div data-testid={'field-bankCode'}>
                      {t('manager.webcastSetup.viewerAccess.registration.fieldsOptions.bankCode')}
                    </div>
                  )}
                </div>
                <LinkStyleButton
                  data-testid="edit-form-button"
                  className={styles.editFormButton}
                  onClick={() => setFieldsModalOpen(true)}
                >
                  {t('manager.webcastSetup.viewerAccess.registration.editFormButton')}
                </LinkStyleButton>
              </div>
            )}
          </div>

          <Tooltip
            label={t(
              'manager.webcastSetup.viewerAccess.registration.requireEmailVerification.disabledEmailVerificationTooltip'
            )}
            hidden={registrationEnabled}
          >
            <SelectionInputLayout
              variant="ghost"
              checked={requireEmailVerification === true}
              inputElement={
                <Toggle
                  data-testid="require-email-verification-toggle"
                  checked={requireEmailVerification === true}
                  disabled={registrationEnabled !== true}
                  onChange={onEmailVerificationChange}
                >
                  {t('manager.webcastSetup.viewerAccess.registration.requireEmailVerification.label')}
                </Toggle>
              }
            />
          </Tooltip>
        </div>
      </fieldset>

      {fieldsModalOpen && (
        <RegistrationFieldsModal
          onConfirm={handleConfirm}
          onClose={() => setFieldsModalOpen(false)}
          registrationForm={registrationForm}
        />
      )}
    </>
  );
}
